import { toLonLat } from 'ol/proj.js';
import { API_URL } from './config.js';
import { getSelectedCountryAbbreviation } from './country-language.js';
import { secureFetch } from './securityUtil.js';
import { selectPCFeaturesByPostcodes } from './pcSelection.js';

const DTM_TYPES = { time: 'drivetime', distance: 'drivedistance', radius: 'radius' };

function getCountryAndLevel() {
  const applicationCountry = String(getSelectedCountryAbbreviation() || '').toUpperCase();
  if (['NL', 'NLD'].includes(applicationCountry)) return { country: 'NL', apiLevel: 'pc4', selectionLevel: 'pc4' };
  // The DTM API classifies Belgium's four-digit result codes as `pc4`.
  // On the map these results belong to the Belgium PostalCode layer.
  if (['BE', 'BEL'].includes(applicationCountry)) return { country: 'BE', apiLevel: 'pc4', selectionLevel: 'postalcode' };
  throw new Error(`DTM analysis is not configured for country ${applicationCountry || 'Unknown'}`);
}

function getPoiLonLat(feature) {
  const coordinate = feature?.getGeometry?.()?.getCoordinates?.();
  if (!Array.isArray(coordinate) || coordinate.length < 2) throw new Error('The selected POI has no valid coordinate');
  return toLonLat(coordinate);
}

export async function runPoiDtmAnalysis({ mode, value, feature }) {
  const type = DTM_TYPES[mode];
  if (!type) throw new Error(`Unsupported DTM analysis mode: ${mode}`);
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) throw new Error('Enter a value greater than zero');
  const { country, apiLevel, selectionLevel } = getCountryAndLevel();
  let requestBody;
  if (mode === 'radius') {
    const poiSetId = feature?.get?.('poiSetId');
    const identifier = feature?.get?.('identifier');
    const poiIdentifier = Number(identifier);
    if (poiSetId && identifier != null && String(identifier).trim() !== '' && Number.isSafeInteger(poiIdentifier)) {
      requestBody = { type, value: numericValue, poi_set_id: poiSetId, pois: [poiIdentifier], level: apiLevel };
    } else {
      // Search results and uploaded POIs without an identifier still have a
      // valid selected point. Radius can use it without selecting a whole set.
      const [longitude, latitude] = getPoiLonLat(feature);
      requestBody = { type, value: numericValue, lat: latitude, long: longitude, level: apiLevel };
    }
  } else {
    const [longitude, latitude] = getPoiLonLat(feature);
    requestBody = { type, value: numericValue, lat: latitude, long: longitude, level: apiLevel, country };
  }
  const endpoint = mode === 'radius' ? 'dtm/poi' : 'dtm';
  const response = await secureFetch(`${API_URL}${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestBody) });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) throw new Error(payload?.message || `DTM request failed (${response.status})`);
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  const postcodes = [...new Set(rows.map(row => String(row?.postal_code ?? '').trim()).filter(Boolean))];
  const selectedFeatures = await selectPCFeaturesByPostcodes(selectionLevel, postcodes, { fitToResults: true, clearExisting: false, resultRows: rows });
  if (postcodes.length && !selectedFeatures.length) {
    throw new Error(`No matching ${selectionLevel === 'postalcode' ? 'PostalCode' : selectionLevel.toUpperCase()} areas were found for the returned postal codes`);
  }
  return { requestBody, rows, postcodes, selectedFeatures };
}

export async function runPoiSetDtmAnalysis({ mode, value, poiSetId, layers = [], onlySelection = false }) {
  const type = DTM_TYPES[mode];
  if (!type) throw new Error(`Unsupported DTM analysis mode: ${mode}`);
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) throw new Error('Enter a value greater than zero');
  if (!poiSetId) throw new Error('The selected POI set is no longer available');

  const analysisLayers = onlySelection ? layers.filter(layer => layer?.getVisible?.() !== false) : layers;
  if (onlySelection && !analysisLayers.length) throw new Error('No visible POI categories are selected');
  const categories = [...new Set(analysisLayers.map(layer => String(layer?.get?.('poiCategory') || layer?.get?.('title') || '').trim()).filter(Boolean))];
  const pois = [...new Set(analysisLayers.flatMap(layer => layer?.getSource?.()?.getFeatures?.() || []).map(feature => feature?.get?.('identifier')).filter(identifier => identifier !== null && identifier !== undefined && String(identifier).trim() !== '').map(identifier => {
    const numericIdentifier = Number(identifier);
    return Number.isFinite(numericIdentifier) ? numericIdentifier : identifier;
  }))];
  if (!categories.length) throw new Error('The selected POI set has no categories');
  if (!pois.length) throw new Error('The selected POI set has no valid POIs');

  const requestBody = { type, value: numericValue, poiSetId, categories, pois };
  const response = await secureFetch(`${API_URL}dtm/poi`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestBody) });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) throw new Error(payload?.message || `POI set DTM request failed (${response.status})`);

  const rows = Array.isArray(payload?.data) ? payload.data : [];
  const resultRows = rows.flatMap(row => Array.isArray(row?.results) ? row.results : [row]);
  const postcodes = [...new Set(resultRows.map(result => String(result?.postal_code ?? '').trim()).filter(Boolean))];
  if (!postcodes.length) throw new Error('POI set DTM response contains no postal codes');
  const { selectionLevel, country } = getCountryAndLevel();
  const selectedFeatures = await selectPCFeaturesByPostcodes(selectionLevel, postcodes, { fitToResults: true, clearExisting: false, resultRows });
  if (!selectedFeatures.length) throw new Error(`No matching ${selectionLevel === 'postalcode' ? 'PostalCode' : selectionLevel.toUpperCase()} areas were found for the returned postal codes`);
  return { requestBody, rows: resultRows, postcodes, selectedFeatures, country, level: selectionLevel };
}
